import { useState, useCallback } from 'react';
import {
  ConditionButtonGroup,
  PAINT_BUTTON_OPTIONS,
  GROWTH_BUTTON_OPTIONS,
  buttonValueToSliderValue,
  sliderValueToButtonValue,
} from './ConditionButtonGroup';

// Legacy slider value arrays - kept for backward compatibility
const PAINT_CONDITIONS = [
  { value: 0, label: 'Not Inspected', dataValue: 'not_inspected' },
  { value: 1, label: 'Excellent', dataValue: 'excellent' },
  { value: 2, label: 'Exc-Good', dataValue: 'excellent-good' },
  { value: 3, label: 'Good', dataValue: 'good' },
  { value: 4, label: 'Good-Fair', dataValue: 'good-fair' },
  { value: 5, label: 'Fair', dataValue: 'fair' },
  { value: 6, label: 'Fair-Poor', dataValue: 'fair-poor' },
  { value: 7, label: 'Poor', dataValue: 'poor' },
  { value: 8, label: 'Poor-Missing', dataValue: 'poor-missing' },
  { value: 9, label: 'Missing', dataValue: 'missing' },
] as const;

const GROWTH_LEVELS = [
  { value: 0, label: 'Not Inspected', dataValue: 'not_inspected' },
  { value: 1, label: 'Minimal', dataValue: 'minimal' },
  { value: 2, label: 'Min-Mod', dataValue: 'minimal-moderate' },
  { value: 3, label: 'Moderate', dataValue: 'moderate' },
  { value: 4, label: 'Mod-Heavy', dataValue: 'moderate-heavy' },
  { value: 5, label: 'Heavy', dataValue: 'heavy' },
  { value: 6, label: 'Heavy-Sev', dataValue: 'heavy-severe' },
  { value: 7, label: 'Severe', dataValue: 'severe' },
  { value: 8, label: 'Severe Max', dataValue: 'severe-maximum' },
] as const;

// Individual propeller condition entry
export interface PropellerConditionEntry {
  number: number;
  condition: string | null;
}

export interface ConditionsData {
  paintCondition: number;
  paintConditionValue: string;
  growthLevel: number;
  growthLevelValue: string;
  propellerConditions: PropellerConditionEntry[];  // Array for multiple props
  thruHullCondition: string | null;
}

// Propeller condition options
const PROPELLER_OPTIONS = [
  { value: 'good', label: 'Good', color: '#22c55e' },
  { value: 'fair', label: 'Fair', color: '#eab308' },
  { value: 'poor', label: 'Poor', color: '#ef4444' },
  { value: 'attention', label: 'Attention', color: '#8b5cf6' },
] as const;

// Through-hull condition options
const THRU_HULL_OPTIONS = [
  { value: 'clear', label: 'Clear', color: '#22c55e' },
  { value: 'partial', label: 'Partial', color: '#eab308' },
  { value: 'blocked', label: 'Blocked', color: '#ef4444' },
  { value: 'attention', label: 'Attention', color: '#8b5cf6' },
] as const;

interface ConditionsFormProps {
  initialPaintCondition?: number;
  initialGrowthLevel?: number;
  initialPropellerConditions?: PropellerConditionEntry[];
  initialThruHullCondition?: string | null;
  onChange?: (data: ConditionsData) => void;
  lastPaintCondition?: string | null;  // e.g., 'fair', 'good-fair'
  lastGrowthLevel?: string | null;     // e.g., 'heavy', 'moderate-heavy'
  lastPropellerConditions?: PropellerConditionEntry[];  // Array of last conditions per prop
  lastThruHullCondition?: string | null;
  propellerCount?: number;  // Number of propellers on boat (default 1)
}

export function ConditionsForm({
  initialPaintCondition = 0,
  initialGrowthLevel = 0,
  initialPropellerConditions = [],
  initialThruHullCondition = null,
  onChange,
  lastPaintCondition,
  lastGrowthLevel,
  lastPropellerConditions = [],
  lastThruHullCondition,
  propellerCount = 1,
}: ConditionsFormProps) {
  // Ensure propellerCount is at least 0 and at most 4
  const actualPropCount = Math.max(0, Math.min(4, propellerCount));

  // Store as data values (strings) internally, convert to/from slider values for compatibility
  const [paintValue, setPaintValue] = useState<string | null>(() =>
    sliderValueToButtonValue(initialPaintCondition, 'paint')
  );
  const [growthValue, setGrowthValue] = useState<string | null>(() =>
    sliderValueToButtonValue(initialGrowthLevel, 'growth')
  );

  // Propeller conditions array - initialize based on propellerCount
  const [propellerValues, setPropellerValues] = useState<PropellerConditionEntry[]>(() => {
    const entries: PropellerConditionEntry[] = [];
    for (let i = 1; i <= actualPropCount; i++) {
      const initial = initialPropellerConditions.find(p => p.number === i);
      entries.push({ number: i, condition: initial?.condition || null });
    }
    return entries;
  });

  const [thruHullValue, setThruHullValue] = useState<string | null>(initialThruHullCondition);

  // Helper to emit all values
  const emitChange = useCallback(
    (updates: Partial<{
      paint: string | null;
      growth: string | null;
      propellers: PropellerConditionEntry[];
      thruHull: string | null;
    }>) => {
      const currentPaint = updates.paint !== undefined ? updates.paint : paintValue;
      const currentGrowth = updates.growth !== undefined ? updates.growth : growthValue;
      const currentPropellers = updates.propellers !== undefined ? updates.propellers : propellerValues;
      const currentThruHull = updates.thruHull !== undefined ? updates.thruHull : thruHullValue;

      onChange?.({
        paintCondition: buttonValueToSliderValue(currentPaint, 'paint'),
        paintConditionValue: currentPaint || 'not_inspected',
        growthLevel: buttonValueToSliderValue(currentGrowth, 'growth'),
        growthLevelValue: currentGrowth || 'not_inspected',
        propellerConditions: currentPropellers,
        thruHullCondition: currentThruHull,
      });
    },
    [paintValue, growthValue, propellerValues, thruHullValue, onChange]
  );

  const handlePaintChange = useCallback(
    (value: string | null) => {
      setPaintValue(value);
      emitChange({ paint: value });
    },
    [emitChange]
  );

  const handleGrowthChange = useCallback(
    (value: string | null) => {
      setGrowthValue(value);
      emitChange({ growth: value });
    },
    [emitChange]
  );

  // Handle propeller change for a specific propeller number
  const handlePropellerChange = useCallback(
    (propNumber: number, value: string | null) => {
      setPropellerValues(prev => {
        const updated = prev.map(p =>
          p.number === propNumber ? { ...p, condition: value } : p
        );
        // Emit change with updated array
        emitChange({ propellers: updated });
        return updated;
      });
    },
    [emitChange]
  );

  const handleThruHullChange = useCallback(
    (value: string | null) => {
      setThruHullValue(value);
      emitChange({ thruHull: value });
    },
    [emitChange]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <ConditionButtonGroup
        id="paintConditionButtons"
        label="Paint Condition"
        options={PAINT_BUTTON_OPTIONS}
        value={paintValue}
        onChange={handlePaintChange}
        allowRange={true}
        colorScale="paint"
        lastValue={lastPaintCondition}
      />

      <ConditionButtonGroup
        id="growthLevelButtons"
        label="Growth Level"
        options={GROWTH_BUTTON_OPTIONS}
        value={growthValue}
        onChange={handleGrowthChange}
        allowRange={true}
        colorScale="growth"
        lastValue={lastGrowthLevel}
      />

      {/* Propeller Conditions - render one card per propeller */}
      {propellerValues.map((prop) => {
        const lastCondition = lastPropellerConditions.find(p => p.number === prop.number)?.condition;
        const label = actualPropCount === 1 ? 'Propeller' : `Propeller ${prop.number}`;

        return (
          <div key={prop.number} style={{ marginTop: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>{label}</span>
              {lastCondition && (
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                  Last: {lastCondition}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {PROPELLER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handlePropellerChange(prop.number, prop.condition === option.value ? null : option.value)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: '2px solid',
                    borderColor: prop.condition === option.value ? option.color : '#e5e7eb',
                    backgroundColor: prop.condition === option.value ? `${option.color}15` : 'white',
                    color: prop.condition === option.value ? option.color : '#6b7280',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {/* Through-Hull Condition */}
      <div style={{ marginTop: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Through-Hulls</span>
          {lastThruHullCondition && (
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>
              Last: {lastThruHullCondition}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {THRU_HULL_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleThruHullChange(thruHullValue === option.value ? null : option.value)}
              style={{
                flex: 1,
                padding: '10px',
                fontSize: '14px',
                fontWeight: 500,
                border: '2px solid',
                borderColor: thruHullValue === option.value ? option.color : '#e5e7eb',
                backgroundColor: thruHullValue === option.value ? `${option.color}15` : 'white',
                color: thruHullValue === option.value ? option.color : '#6b7280',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Export constants for use elsewhere
export { PAINT_CONDITIONS, GROWTH_LEVELS };
